/**
 * Real backend adapter for the Real Time board.
 *
 * It only uses endpoints that already exist, so the backend flow is unchanged:
 *   - GET  /Job/Realtime/                     online workers (+ live coordinates), unassigned slots
 *   - GET  /JobAssignment/List/?date_from=…   per-worker slots (JobAssignment) for the day's jobs;
 *                                             status pending/accepted/rejected/auto_rejected/…
 *   - GET  workday entries ?work_date=         check-in / lateness per worker
 *   - GET  workers list                        phone, avatar and last-seen for every worker
 *   - GET  /Job/List/ (+ job detail)           job coordinates, address and category
 *   - POST /JobAssignment/Update|Add|Delete     add / remove a worker
 *
 * How the backend maps to the board: a job's "required workers" is its number of assignment
 * slots; an empty slot (worker = null) is an open position. Nearby workers are ranked in the
 * browser (no candidates endpoint yet). TODO(backend) marks what the target contract in
 * data-source.ts adds.
 *
 * Request budget (see REFRESH_POLICY in ../config.ts): each list is cached and shared, only
 * Job/Realtime is polled, assignments/check-ins are re-read when Job/Realtime shows a change
 * (or after 2 min), workers/jobs after 5 min, and per-job details of overdue jobs load in the
 * background once, then again only when their open slots change.
 */
import { assignmentsApi } from '@/services/assignments'
import { dashboardApi } from '@/services/dashboard'
import { jobsApi } from '@/services/jobs'
import { workersApi } from '@/services/workers'
import { getApiBaseUrl } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import type { Assignment, Job as ApiJob, Worker as ApiWorker, WorkDayEntry } from '@/types'

import { REFRESH_POLICY, realtimeConfig } from '../config'
import {
  ANY_CATEGORY,
  type Job,
  type JobStatus,
  type JobWorker,
  type RealtimeSnapshot,
  type Worker,
  type WorkerJobStatus,
  type WorkerLocationSource,
  type WorkerResponse,
  type WorkerStatus,
} from '../types'
import { toValidCoords, type LatLng } from '../utils/distance'
import { applyGeocodeCache } from '../utils/geocode-cache'
import { getActiveJobWorkers } from '../utils/job-workers'
import { toDateKey } from '../utils/time'
import type { FetchSnapshotParams, RealtimeDataSource } from './data-source'
import { isAbortError, SharedResource } from './shared-resource'
import { createReconnectingSocket } from './socket'

const DEFAULT_JOB_MINUTES = 60
const PAGE_SIZE = 200
const ASSIGNMENT_PAGE_SIZE = 500
/** Max per-job lookups per refresh (overdue slots from earlier days). */
const MAX_JOB_LOOKUPS = 20
/** A location ping newer than this, from an online worker, counts as live. */
const LIVE_LOCATION_WINDOW_MS = 10 * 60_000

const JOB_PREFIX = 'job:'

const toStringOrNull = (value: unknown): string | null => {
  const text = String(value ?? '').trim()
  return text ? text : null
}

const addMinutes = (iso: string, minutes: number) => new Date(new Date(iso).getTime() + minutes * 60_000).toISOString()

const backendJobIdOf = (job: Pick<Job, 'id'>) => (job.id.startsWith(JOB_PREFIX) ? job.id.slice(JOB_PREFIX.length) : job.id)

/** JobAssignment.status -> offer response; null = slot no longer counts (withdrawn/cancelled). */
function mapAssignmentResponse(status: Assignment['status']): WorkerResponse | null {
  switch (status) {
    case 'accepted':
    case 'completed':
      return 'accepted'
    case 'rejected':
      return 'declined'
    case 'auto_rejected':
      return 'expired'
    case 'withdrawn':
    case 'cancelled':
      return null
    default:
      return 'pending'
  }
}

function mapEntryWorkerStatus(entry: WorkDayEntry | undefined, assignmentStatus: Assignment['status']): WorkerJobStatus {
  if (assignmentStatus === 'completed') return 'completed'
  if (!entry) return 'assigned'
  const status = String(entry.status ?? '')
  if (['completed', 'accepted', 'logged'].includes(status) || entry.check_out) return 'completed'
  if (status === 'in_progress') return 'started'
  if (entry.check_in) return 'checked_in'
  // TODO(backend): emit an "on the way" signal from the worker app.
  return 'assigned'
}

/**
 * The worker's answer is recorded on the workday entry (worker_schedule_status) in this
 * backend; the assignment status only moves for completion/withdrawal. Prefer the entry.
 */
function resolveResponse(assignmentResponse: WorkerResponse, entry: WorkDayEntry | undefined): WorkerResponse {
  if (assignmentResponse === 'accepted' || assignmentResponse === 'expired') return assignmentResponse
  switch (entry?.worker_schedule_status) {
    case 'accepted':
      return 'accepted'
    case 'rejected':
      return 'declined'
    default:
      return assignmentResponse
  }
}

interface JobGeo {
  coords: LatLng | null
  address: string | null
  area: string | null
  categoryId: string | null
  categoryName: string | null
}

const toJobGeo = (apiJob: ApiJob): JobGeo => ({
  coords: toValidCoords(apiJob.latitude, apiJob.longitude),
  address: toStringOrNull(apiJob.address),
  area: toStringOrNull(apiJob.location) ?? toStringOrNull(apiJob.city),
  categoryId: toStringOrNull(apiJob.category),
  categoryName: toStringOrNull(apiJob.category_name),
})

/**
 * Categories a worker can do: their category list plus the categories of their active skills.
 * A skill without a category applies to every category (same rule as the backend).
 */
function resolveWorkerCategories(apiWorker: ApiWorker): string[] {
  const categories = new Set((apiWorker.categories ?? []).map((id) => String(id)))
  ;(apiWorker.worker_skill_mappings ?? []).forEach((mapping) => {
    if (mapping.is_active === false) return
    categories.add(mapping.job_category_id == null || mapping.job_category_id === '' ? ANY_CATEGORY : String(mapping.job_category_id))
  })
  return Array.from(categories)
}

/** Per-refresh bookkeeping needed to turn board actions back into assignment calls. */
interface SlotIndex {
  /** backend job id -> empty slot assignment ids */
  openSlots: Map<string, string[]>
}

function buildJobs(
  assignments: Assignment[],
  entries: WorkDayEntry[],
  geoByJobId: Map<string, JobGeo>,
  now: number,
): { jobs: Job[]; slots: SlotIndex } {
  const entryByJobWorker = new Map(entries.map((entry) => [`${entry.job}|${entry.worker}`, entry]))
  const byJob = new Map<string, Assignment[]>()
  assignments.forEach((assignment) => {
    const jobId = toStringOrNull(assignment.job_id)
    if (!jobId) return
    const list = byJob.get(jobId) ?? []
    list.push(assignment)
    byJob.set(jobId, list)
  })

  const openSlots = new Map<string, string[]>()
  const jobs: Job[] = []

  byJob.forEach((rows, backendJobId) => {
    const slots = rows.filter((row) => mapAssignmentResponse(row.status) !== null || !row.worker_id)
    if (slots.length === 0) return
    const first = rows[0]
    const start = first.start_date || first.scheduled_start
    if (!start) return

    const workers: JobWorker[] = []
    const empty: string[] = []
    slots.forEach((row) => {
      const workerId = toStringOrNull(row.worker_id)
      const response = mapAssignmentResponse(row.status)
      if (!workerId || !response) {
        empty.push(String(row.id))
        return
      }
      if (workers.some((worker) => worker.workerId === workerId)) return
      const entry = entryByJobWorker.get(`${backendJobId}|${workerId}`)
      workers.push({
        workerId,
        response: resolveResponse(response, entry),
        status: mapEntryWorkerStatus(entry, row.status),
        sentAt: row.created_at,
        respondedAt: response === 'pending' ? null : row.updated_at,
        // TODO(backend): expose when the worker opened the offer and the last reminder time.
        openedAt: null,
        lastReminderAt: null,
        assignmentId: String(row.id),
      })
    })
    openSlots.set(backendJobId, empty)

    const dayEntries = entries.filter((entry) => String(entry.job) === backendJobId)
    const active = getActiveJobWorkers({ workers } as Job, now)
    let status: JobStatus = 'scheduled'
    if (rows.every((row) => row.status === 'completed')) status = 'completed'
    else if (active.some((worker) => worker.status === 'started' || worker.status === 'checked_in')) status = 'in_progress'
    else if (active.length === 0) status = 'unassigned'
    else if (dayEntries.some((entry) => entry.is_late && !entry.check_in)) status = 'late'

    const geo = geoByJobId.get(backendJobId)
    const job: Job = {
      id: `${JOB_PREFIX}${backendJobId}`,
      bookingNo: first.job_ref || `#${backendJobId}`,
      service: first.job_title || first.role_label || 'Job',
      area: geo?.area || dayEntries[0]?.job_location_name || '',
      lat: geo?.coords?.lat ?? null,
      lng: geo?.coords?.lng ?? null,
      address: geo?.address ?? null,
      categoryId: geo?.categoryId ?? null,
      categoryName: geo?.categoryName ?? null,
      scheduledStart: start,
      scheduledEnd: first.end_date || dayEntries[0]?.planned_end || addMinutes(start, DEFAULT_JOB_MINUTES),
      status,
      requiredWorkers: Math.max(1, workers.filter((worker) => worker.response !== 'declined' && worker.response !== 'expired').length + empty.length),
      workers,
    }
    // Addresses geocoded on demand in the browser are reused until the backend stores coordinates.
    jobs.push(applyGeocodeCache(job))
  })

  return { jobs, slots: { openSlots } }
}

/**
 * Worker location fallback: live (online + recent ping) -> last known ping -> base
 * (profile) location -> none. 0,0 placeholders are treated as missing.
 */
function resolveWorkerLocation(apiWorker: ApiWorker, isOnline: boolean, now: number): { coords: LatLng | null; source: WorkerLocationSource | null } {
  const lastPing = toValidCoords(apiWorker.last_latitude, apiWorker.last_longitude)
  if (lastPing) {
    const pingAt = Date.parse(String(apiWorker.last_location_updated_at ?? ''))
    const isRecent = Number.isFinite(pingAt) && now - pingAt <= LIVE_LOCATION_WINDOW_MS
    return { coords: lastPing, source: isOnline && isRecent ? 'live' : 'last_known' }
  }
  // TODO(backend): expose the shift start / branch base location as a further fallback.
  const base = toValidCoords(apiWorker.permanent_latitude, apiWorker.permanent_longitude)
  return base ? { coords: base, source: 'base' } : { coords: null, source: null }
}

type OverviewPayload = NonNullable<Awaited<ReturnType<typeof dashboardApi.getRealtimeOverview>>>

function resolveWorkerStatus(workerId: string, online: boolean, jobs: Job[], now: number): WorkerStatus {
  if (!online) return 'offline'
  const own = jobs.filter((job) => getActiveJobWorkers(job, now).some((worker) => worker.workerId === workerId))
  if (own.some((job) => job.status === 'late')) return 'late'
  if (own.some((job) => job.status === 'in_progress' || job.status === 'en_route')) return 'on_job'
  return 'available'
}

function mapWorkers(overview: OverviewPayload, apiWorkers: ApiWorker[], jobs: Job[], leaveWorkerIds: Set<string>, now: number): Worker[] {
  const online = new Map(overview.online_workers.map((item) => [String(item.worker_id ?? item.id), item]))
  const byId = new Map<string, Worker>()
  const currentJobOf = (id: string) => jobs.find((job) => job.status === 'in_progress' && job.workers.some((worker) => worker.workerId === id))

  apiWorkers.forEach((apiWorker) => {
    const id = String(apiWorker.worker_id ?? apiWorker.id)
    let location = resolveWorkerLocation(apiWorker, online.has(id), now)
    let lastSeenAt = toStringOrNull(apiWorker.last_location_updated_at)
    // The worker list is cached for minutes; Job/Realtime (polled) carries the same last
    // location for online workers. A different position there is a newer ping.
    const live = online.get(id)
    const liveCoords = live ? toValidCoords(live.latitude, live.longitude) : null
    if (liveCoords && (liveCoords.lat !== location.coords?.lat || liveCoords.lng !== location.coords?.lng)) {
      location = { coords: liveCoords, source: 'live' }
      lastSeenAt = toStringOrNull(overview.generated_at) ?? lastSeenAt
    }
    byId.set(id, {
      id,
      name: apiWorker.display_name || apiWorker.full_name || apiWorker.worker_name || `Worker ${id}`,
      phone: toStringOrNull(apiWorker.phone ?? apiWorker.phone_number),
      avatarUrl: toStringOrNull(apiWorker.avatar_url ?? apiWorker.avatar),
      status: resolveWorkerStatus(id, online.has(id), jobs, now),
      lat: location.coords?.lat ?? null,
      lng: location.coords?.lng ?? null,
      locationSource: location.source,
      lastSeenAt,
      currentJobId: currentJobOf(id)?.id ?? null,
      // TODO(backend): expose a worker rating to rank assignment candidates.
      rating: null,
      onLeave: leaveWorkerIds.has(id),
      categories: resolveWorkerCategories(apiWorker),
    })
  })

  // Online workers missing from the (paginated) list are still shown.
  online.forEach((item, id) => {
    if (byId.has(id)) return
    const coords = toValidCoords(item.latitude, item.longitude)
    byId.set(id, {
      id,
      name: item.worker_name || `Worker ${id}`,
      phone: null,
      avatarUrl: null,
      status: resolveWorkerStatus(id, true, jobs, now),
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      locationSource: coords ? 'last_known' : null,
      lastSeenAt: null,
      currentJobId: currentJobOf(id)?.id ?? null,
      rating: null,
      onLeave: leaveWorkerIds.has(id),
    })
  })

  return Array.from(byId.values())
}

/** TODO(backend): confirm the WebSocket auth scheme; this sends the JWT + branch as query params. */
function buildSocketUrl(serviceInstanceId: string | null): string {
  const raw = realtimeConfig.wsUrl
  const base = /^wss?:\/\//i.test(raw) ? raw : new URL(raw, getApiBaseUrl().replace(/^http/i, 'ws')).toString()
  const url = new URL(base)
  if (serviceInstanceId) url.searchParams.set('service_instance_id', serviceInstanceId)
  const token = useAuthStore.getState().accessToken
  if (token) url.searchParams.set('token', token)
  return url.toString()
}

const ALL = 'all'
const OVERDUE_SLOT_PAGE_SIZE = 50

/** Rejects payloads the board cannot use (so the caller falls back to a full refresh). */
function assertOverview(value: OverviewPayload | null): OverviewPayload {
  if (!value || !Array.isArray(value.online_workers) || !Array.isArray(value.unassigned_assignments)) {
    throw new Error('Job/Realtime returned an unexpected response.')
  }
  return value
}

/**
 * What in Job/Realtime moves when staffing or check-ins change. When it differs from the
 * value at the last assignments fetch, the assignments and work-day entries are re-read.
 * (Online counts are left out: they change constantly and do not affect staffing.)
 */
export function staffingFingerprint(overview: OverviewPayload): string {
  const { online_workers_count: _online, active_workers_count: _active, ...summary } = overview.summary ?? ({} as OverviewPayload['summary'])
  const ids = (items: unknown) => (Array.isArray(items) ? (items as Array<Record<string, unknown>>) : [])
    .map((item) => `${String(item.id)}:${String(item.status ?? '')}:${String(item.worker_id ?? '')}`)
    .sort()
    .join(',')
  return JSON.stringify([
    summary,
    ids(overview.unassigned_assignments),
    ids(overview.live_workday_entries),
    ids(overview.late_work_day_entries),
    ids(overview.no_show_work_day_entries),
    ids(overview.attention_work_day_entries),
  ])
}

type UnassignedItem = OverviewPayload['unassigned_assignments'][number]

/** Unassigned slots from earlier days, grouped by job (the overdue jobs). */
export function groupOverdueItems(overview: OverviewPayload, dateKey: string, timeZone: string): Map<string, UnassignedItem[]> {
  const byJob = new Map<string, UnassignedItem[]>()
  overview.unassigned_assignments.forEach((item) => {
    if (item.job_id == null || !item.scheduled_start || toDateKey(item.scheduled_start, timeZone) >= dateKey) return
    const jobId = String(item.job_id)
    byJob.set(jobId, [...(byJob.get(jobId) ?? []), item])
  })
  return byJob
}

/** Changes when an overdue job's open slots change (re-fetch its details then). */
export const overdueFingerprint = (items: UnassignedItem[]) => items.map((item) => String(item.id)).sort().join(',')

/** Overdue job shown straight from Job/Realtime until its slots/details have loaded. */
export function buildOverdueJob(jobId: string, items: UnassignedItem[], geo: JobGeo | undefined): Job {
  const first = [...items].sort((a, b) => String(a.scheduled_start).localeCompare(String(b.scheduled_start)))[0]
  const start = String(first.scheduled_start)
  return applyGeocodeCache({
    id: `${JOB_PREFIX}${jobId}`,
    bookingNo: first.job_ref || `#${jobId}`,
    service: first.job_title || first.role_label || 'Job',
    area: geo?.area || '',
    lat: geo?.coords?.lat ?? null,
    lng: geo?.coords?.lng ?? null,
    address: geo?.address ?? null,
    categoryId: geo?.categoryId ?? null,
    categoryName: geo?.categoryName ?? null,
    scheduledStart: start,
    scheduledEnd: addMinutes(start, DEFAULT_JOB_MINUTES),
    status: 'unassigned',
    requiredWorkers: Math.max(1, items.length),
    workers: [],
  })
}

const PENDING_SKIP_STATUSES = new Set(['cancelled', 'draft'])

/**
 * A day job from Job/List before its assignments load: shown (card, map pin) with a loading
 * state for its workers. Only jobs that certainly have slots are included — some assigned
 * worker (workers_count) or an open slot listed by Job/Realtime — so none disappear later.
 */
export function buildPendingJob(apiJob: ApiJob, geo: JobGeo | undefined): Job | null {
  const start = apiJob.scheduled_start
  if (!start || PENDING_SKIP_STATUSES.has(String(apiJob.status))) return null
  const status: JobStatus = apiJob.status === 'completed' ? 'completed' : apiJob.status === 'in_progress' ? 'in_progress' : 'scheduled'
  return applyGeocodeCache({
    id: `${JOB_PREFIX}${apiJob.id}`,
    bookingNo: apiJob.job_ref || `#${apiJob.id}`,
    service: apiJob.title || 'Job',
    area: geo?.area || '',
    lat: geo?.coords?.lat ?? null,
    lng: geo?.coords?.lng ?? null,
    address: geo?.address ?? null,
    categoryId: geo?.categoryId ?? null,
    categoryName: geo?.categoryName ?? null,
    scheduledStart: start,
    scheduledEnd: apiJob.scheduled_end || addMinutes(start, DEFAULT_JOB_MINUTES),
    status,
    requiredWorkers: Math.max(1, Number(apiJob.workers_count) || 1),
    workers: [],
    staffingPending: true,
  })
}

/** Everything a snapshot is assembled from; lists are null until first loaded. */
export interface SnapshotInputs {
  overview: OverviewPayload
  dayJobs: ApiJob[] | null
  apiWorkers: ApiWorker[] | null
  dayAssignments: Assignment[] | null
  entries: WorkDayEntry[] | null
  /** Slots of overdue jobs whose details have loaded, by backend job id. */
  overdueSlots: Map<string, Assignment[]>
  /** Job/Get results for jobs missing from the Job/List page, by backend job id. */
  jobDetails: Map<string, ApiJob>
  staffingAsOf: number | null
}

export function assembleSnapshot(
  inputs: SnapshotInputs,
  { dateKey, timeZone, now }: { dateKey: string; timeZone: string; now: number },
): { snapshot: RealtimeSnapshot; slots: SlotIndex; overdueJobIds: string[]; jobsMissingGeo: string[] } {
  const { overview, dayJobs, apiWorkers, dayAssignments, entries, overdueSlots, jobDetails } = inputs
  const geoByJobId = new Map<string, JobGeo>()
  dayJobs?.forEach((apiJob) => geoByJobId.set(String(apiJob.id), toJobGeo(apiJob)))
  jobDetails.forEach((apiJob, id) => {
    if (!geoByJobId.has(id)) geoByJobId.set(id, toJobGeo(apiJob))
  })

  const staffingLoaded = dayAssignments != null && entries != null
  const dayJobIds = new Set((dayAssignments ?? []).map((row) => String(row.job_id)))
  const overdueItems = groupOverdueItems(overview, dateKey, timeZone)
  const overdueJobIds = Array.from(overdueItems.keys()).filter((id) => !dayJobIds.has(id)).slice(0, MAX_JOB_LOOKUPS)

  const jobs: Job[] = []
  const openSlots = new Map<string, string[]>()

  // Overdue jobs: from their loaded slots, else straight from Job/Realtime.
  const loadedOverdueRows = overdueJobIds.flatMap((id) => overdueSlots.get(id) ?? [])
  const rows = [...(dayAssignments ?? []), ...loadedOverdueRows]
  const built = buildJobs(rows, entries ?? [], geoByJobId, now)
  built.jobs.forEach((job) => jobs.push(job))
  built.slots.openSlots.forEach((value, key) => openSlots.set(key, value))
  overdueJobIds.forEach((id) => {
    if (overdueSlots.has(id)) return
    const items = overdueItems.get(id) as UnassignedItem[]
    jobs.push(buildOverdueJob(id, items, geoByJobId.get(id)))
    openSlots.set(id, items.map((item) => String(item.id)))
  })

  if (!staffingLoaded) {
    const withOpenSlots = new Set(overview.unassigned_assignments.map((item) => String(item.job_id)))
    const known = new Set(jobs.map((job) => job.id))
    dayJobs?.forEach((apiJob) => {
      const id = String(apiJob.id)
      if (known.has(`${JOB_PREFIX}${id}`)) return
      if (!(Number(apiJob.workers_count) > 0) && !withOpenSlots.has(id)) return
      const job = buildPendingJob(apiJob, geoByJobId.get(id))
      if (job) jobs.push(job)
    })
  }

  const jobsMissingGeo = staffingLoaded
    ? Array.from(dayJobIds).filter((id) => !geoByJobId.has(id)).slice(0, MAX_JOB_LOOKUPS)
    : []
  const leaveWorkerIds = new Set((overview.worker_day_leaves ?? []).filter((leave) => leave.is_on_leave).map((leave) => String(leave.worker_id)))
  const entryTimeZone = (entries ?? []).find((entry) => entry.org_timezone || entry.org_time_zone)

  return {
    snapshot: {
      workers: mapWorkers(overview, apiWorkers ?? [], jobs, leaveWorkerIds, now),
      jobs,
      generatedAt: overview.generated_at,
      timeZone: entryTimeZone?.org_timezone || entryTimeZone?.org_time_zone || null,
      workHours: null,
      staffingLoaded,
      staffingAsOf: inputs.staffingAsOf ?? undefined,
    },
    slots: { openSlots },
    overdueJobIds,
    jobsMissingGeo,
  }
}

/** Runs `task` over `items`, at most `limit` at a time. */
async function runLimited<T>(items: T[], limit: number, task: (item: T) => Promise<void>) {
  let next = 0
  const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next]
      next += 1
      await task(item)
    }
  })
  await Promise.all(lanes)
}

export function createApiRealtimeSource({ serviceInstanceId }: { serviceInstanceId: string | null }): RealtimeDataSource {
  let slotIndex: SlotIndex = { openSlots: new Map() }
  const policy = REFRESH_POLICY

  // One shared, cached resource per dataset (the key is the day, or ALL).
  const overviewRes = new SharedResource<OverviewPayload>((_key, signal) => dashboardApi.getRealtimeOverview({ signal }).then(assertOverview))
  const dayJobsRes = new SharedResource<ApiJob[]>((day, signal) => jobsApi
    .getJobs({ date_filter: 'custom', start_date: day, end_date: day, page_size: PAGE_SIZE }, { signal })
    .then((page) => page?.results ?? []))
  const workersRes = new SharedResource<ApiWorker[]>((_key, signal) => workersApi
    .getWorkers({ page_size: PAGE_SIZE }, { signal })
    .then((page) => page?.results ?? []))
  const assignmentsRes = new SharedResource<Assignment[]>((day, signal) => assignmentsApi
    .getAssignments({ date_from: day, date_to: day, page_size: ASSIGNMENT_PAGE_SIZE }, { signal })
    .then((page) => page?.results ?? []))
  const entriesRes = new SharedResource<WorkDayEntry[]>((day, signal) => assignmentsApi
    .getWorkDayEntries({ work_date: day, page_size: PAGE_SIZE }, { signal })
    .then((page) => page?.results ?? []))

  // Per-job lookups (overdue jobs' slots, Job/Get for missing coordinates), cached by job id.
  const overdueSlotRes = new Map<string, { resource: SharedResource<Assignment[]>; fingerprint: string; changedAt: number }>()
  const jobDetailRes = new Map<string, SharedResource<ApiJob>>()
  const overdueSlotResource = (jobId: string) => {
    let entry = overdueSlotRes.get(jobId)
    if (!entry) {
      entry = {
        resource: new SharedResource<Assignment[]>((_key, signal) => assignmentsApi
          .getAssignments({ job_id: jobId, page_size: OVERDUE_SLOT_PAGE_SIZE }, { signal })
          .then((page) => page?.results ?? [])),
        fingerprint: '',
        changedAt: 0,
      }
      overdueSlotRes.set(jobId, entry)
    }
    return entry
  }
  const jobDetailResource = (jobId: string) => {
    let resource = jobDetailRes.get(jobId)
    if (!resource) {
      resource = new SharedResource<ApiJob>((_key, signal) => jobsApi.getJob(jobId, { signal }))
      jobDetailRes.set(jobId, resource)
    }
    return resource
  }

  /** Job/Realtime fingerprint at the last assignments fetch. */
  let staffingFingerprintAtFetch: string | null = null

  function assembleFromCache(dateKey: string, timeZone: string) {
    const overview = overviewRes.peek(ALL)?.value
    if (!overview) return null
    const overdueSlots = new Map<string, Assignment[]>()
    overdueSlotRes.forEach((entry, id) => {
      const value = entry.resource.peek(ALL)?.value
      if (value) overdueSlots.set(id, value)
    })
    const jobDetails = new Map<string, ApiJob>()
    jobDetailRes.forEach((resource, id) => {
      const value = resource.peek(ALL)?.value
      if (value) jobDetails.set(id, value)
    })
    const assembled = assembleSnapshot({
      overview,
      dayJobs: dayJobsRes.peek(dateKey)?.value ?? null,
      apiWorkers: workersRes.peek(ALL)?.value ?? null,
      dayAssignments: assignmentsRes.peek(dateKey)?.value ?? null,
      entries: entriesRes.peek(dateKey)?.value ?? null,
      overdueSlots,
      jobDetails,
      staffingAsOf: assignmentsRes.peek(dateKey)?.requestedAt ?? null,
    }, { dateKey, timeZone, now: Date.now() })
    slotIndex = assembled.slots
    return assembled
  }

  /**
   * Overdue jobs' slots + coordinates, and coordinates of day jobs missing from Job/List:
   * in the background, 2 at a time, cached per job; each result is pushed as a progress snapshot.
   */
  function loadDetailsInBackground(
    { dateKey, timeZone, signal, onProgress, freshAfter }: { dateKey: string; timeZone: string; signal?: AbortSignal; onProgress?: FetchSnapshotParams['onProgress']; freshAfter?: number },
  ) {
    const assembled = assembleFromCache(dateKey, timeZone)
    const overview = overviewRes.peek(ALL)?.value
    if (!assembled || !overview) return
    const overdueItems = groupOverdueItems(overview, dateKey, timeZone)
    const tasks: Array<() => Promise<unknown>> = []

    assembled.overdueJobIds.forEach((jobId) => {
      const entry = overdueSlotResource(jobId)
      const fingerprint = overdueFingerprint(overdueItems.get(jobId) ?? [])
      if (entry.fingerprint !== fingerprint) {
        // Its open slots changed since the last fetch: re-read it now.
        if (entry.fingerprint) entry.changedAt = Date.now()
        entry.fingerprint = fingerprint
      }
      const options = { maxAgeMs: policy.overdueDetailsTtlMs, freshAfter: freshAfter ?? (entry.changedAt || undefined), signal }
      if (!entry.resource.isFresh(ALL, options)) tasks.push(() => entry.resource.get(ALL, options))
      const detail = jobDetailResource(jobId)
      const detailOptions = { maxAgeMs: policy.overdueDetailsTtlMs, freshAfter, signal }
      if (!detail.isFresh(ALL, detailOptions)) tasks.push(() => detail.get(ALL, detailOptions))
    })
    assembled.jobsMissingGeo.forEach((jobId) => {
      const detail = jobDetailResource(jobId)
      const options = { maxAgeMs: policy.jobDetailsTtlMs, freshAfter, signal }
      if (!detail.isFresh(ALL, options)) tasks.push(() => detail.get(ALL, options))
    })
    if (tasks.length === 0) return

    void runLimited(tasks, policy.detailConcurrency, async (task) => {
      try {
        await task()
      } catch {
        return // a failed lookup keeps the job's placeholder / previous details
      }
      if (signal?.aborted) return
      const next = assembleFromCache(dateKey, timeZone)
      if (next) onProgress?.(next.snapshot)
    })
  }

  async function fetchSnapshot({ dateKey, timeZone, reason = 'poll', signal, onProgress }: FetchSnapshotParams): Promise<RealtimeSnapshot> {
    const requestedAt = Date.now()
    // Refresh / Retry: nothing cached is good enough.
    const forceAll = reason === 'manual'
    const freshAll = forceAll ? requestedAt : undefined
    const progress = (snapshot: RealtimeSnapshot | undefined) => {
      if (snapshot && !signal?.aborted) onProgress?.(snapshot)
    }
    /** Lists the board can work without: on failure keep the last value (or none). */
    const optional = <T,>(promise: Promise<T>, fallback: () => T | null): Promise<T | null> =>
      promise.catch((error) => {
        if (isAbortError(error) || signal?.aborted) throw error
        return fallback()
      })

    const referenceOptions = { maxAgeMs: policy.referenceMaxAgeMs, freshAfter: freshAll, signal }
    const staffingOptions = { maxAgeMs: policy.staffingMaxAgeMs, freshAfter: freshAll, signal }

    // 1) Job/Realtime (always re-read, except right after the admin's own change) and Job/List.
    //    On first load / Refresh the slow assignments list starts at once, in parallel.
    const overviewPromise = reason === 'mutation' && overviewRes.peek(ALL)
      ? Promise.resolve((overviewRes.peek(ALL) as { value: OverviewPayload }).value)
      : overviewRes.get(ALL, { maxAgeMs: 0, freshAfter: freshAll, signal })
    const dayJobsPromise = optional(dayJobsRes.get(dateKey, referenceOptions), () => dayJobsRes.peek(dateKey)?.value ?? null)
    const earlyStaffing = reason === 'initial' || forceAll
    let assignmentsPromise = earlyStaffing ? assignmentsRes.get(dateKey, staffingOptions) : null
    assignmentsPromise?.catch(() => undefined)

    let overviewError: unknown = null
    let overview: OverviewPayload | null = null
    try {
      overview = await overviewPromise
    } catch (error) {
      if (isAbortError(error) || signal?.aborted) throw error
      overviewError = error
    }
    await dayJobsPromise.catch((error) => {
      if (isAbortError(error) || signal?.aborted) throw error
    })

    // 2) Decide what else is stale. A failed / unexpected Job/Realtime response cannot tell us
    //    what changed, so everything is re-read (same one-request-per-dataset guard).
    const fallbackFull = overviewError != null
    const freshStaffingAfter = forceAll || fallbackFull || reason === 'mutation'
      || (overview != null && staffingFingerprintAtFetch != null && staffingFingerprint(overview) !== staffingFingerprintAtFetch)
      ? requestedAt
      : undefined
    const staffing = { ...staffingOptions, freshAfter: freshStaffingAfter }
    const reference = fallbackFull ? { ...referenceOptions, freshAfter: requestedAt } : referenceOptions
    // Job/List was read before the failure was known: re-read it too.
    const dayJobsRefresh = fallbackFull
      ? optional(dayJobsRes.get(dateKey, reference), () => dayJobsRes.peek(dateKey)?.value ?? null)
      : null
    if (!assignmentsPromise || (freshStaffingAfter != null && !earlyStaffing)) assignmentsPromise = assignmentsRes.get(dateKey, staffing)
    assignmentsPromise.catch(() => undefined)

    // First paint: Job/Realtime + Job/List (+ whatever is cached). Workers and check-ins follow,
    // and overdue jobs' details start loading in the background (shared with the call below).
    if (reason === 'initial' || !assignmentsRes.peek(dateKey)) {
      progress(assembleFromCache(dateKey, timeZone)?.snapshot)
      loadDetailsInBackground({ dateKey, timeZone, signal, onProgress, freshAfter: freshAll })
    }

    const newOnlineWorker = overview?.online_workers.some((item) => {
      const cached = workersRes.peek(ALL)?.value
      return cached != null && !cached.some((worker) => String(worker.worker_id ?? worker.id) === String(item.worker_id ?? item.id))
    })
    const workersPromise = optional(
      workersRes.get(ALL, newOnlineWorker ? { ...reference, freshAfter: requestedAt } : reference),
      () => workersRes.peek(ALL)?.value ?? null,
    )
    const entriesOptions = reason === 'mutation' ? staffingOptions : staffing
    const entriesPromise = entriesRes.get(dateKey, entriesOptions)
    entriesPromise.catch(() => undefined)
    // Still no assignments at all (first load): show the workers as soon as they arrive.
    void workersPromise.then(() => {
      if (!assignmentsRes.peek(dateKey)) progress(assembleFromCache(dateKey, timeZone)?.snapshot)
    }, () => undefined)

    // 3) Staffing (the slow part). Its failure fails the refresh (pill: Reconnecting) while the
    //    board keeps showing the previous data.
    await Promise.all([assignmentsPromise, entriesPromise, workersPromise, dayJobsRefresh])
    if (overview) staffingFingerprintAtFetch = staffingFingerprint(overview)
    if (!overview && !overviewRes.peek(ALL)) throw overviewError instanceof Error ? overviewError : new Error('Realtime overview is unavailable.')

    const assembled = assembleFromCache(dateKey, timeZone)
    if (!assembled) throw new Error('Realtime overview is unavailable.')
    loadDetailsInBackground({ dateKey, timeZone, signal, onProgress, freshAfter: freshAll })
    if (overviewError) {
      // Everything else is fresh: show it, but report the failed Job/Realtime refresh.
      progress(assembled.snapshot)
      throw overviewError instanceof Error ? overviewError : new Error('Realtime overview is unavailable.')
    }
    return assembled.snapshot
  }

  return {
    kind: 'api',
    fetchSnapshot,

    addJobWorker: async ({ job, workerId }) => {
      const backendJobId = backendJobIdOf(job)
      const numericWorkerId = Number(workerId)
      const worker = Number.isFinite(numericWorkerId) ? numericWorkerId : workerId
      const openSlot = slotIndex.openSlots.get(backendJobId)?.shift()
      if (openSlot) {
        // Fill an open position (the existing assign flow; the backend notifies the worker).
        await assignmentsApi.updateAssignment(openSlot, { worker } as never)
        return
      }
      // Same payload the Locations page uses to add a worker to a job.
      await assignmentsApi.createAssignment({
        job: backendJobId,
        worker,
        status: 'pending',
        pay_type: 'per_hour',
        pay: 0,
        night_pay: 0,
        holiday_pay: 0,
        peak_pay: 0,
        pay_flat: 0,
      } as never)
    },

    removeJobWorker: async ({ job, workerId }) => {
      const assignmentId = job.workers.find((worker) => worker.workerId === workerId)?.assignmentId
      if (!assignmentId) throw new Error('This worker is no longer on the job.')
      // Existing "remove worker from job" flow. Note: this deletes the slot, so the job's
      // required count drops by one until the target DELETE /workers endpoint exists.
      // TODO(backend): DELETE /api/jobs/:id/workers/:workerId (keeps the slot, notifies the worker).
      await assignmentsApi.deleteAssignment(assignmentId)
    },

    connect: (handlers) => (realtimeConfig.wsUrl ? createReconnectingSocket(buildSocketUrl(serviceInstanceId), handlers) : null),
  }
}
